import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { companiesAPI, devicesAPI, notificationsAPI, boxAssignmentsAPI } from '../services/api';
import { normalizeBatches } from '../utils/batches';
import MapWidget from '../components/MapWidget';
import { useAuthStore } from '../store/authStore';
import { formatDateForTimezone } from '../utils/datetime';
import './SuperadminDashboard.css';

export default function SuperadminDashboard({ isClient = false }) {
  const currentUser = useAuthStore((state) => state.user);
  const timezone = useAuthStore((state) => state.timezone);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    companyId: '',
    depotId: '',
    uploadBatchId: '',
  });

  const [showFiltersPanel, setShowFiltersPanel] = useState(isClient);

  const [metrics, setMetrics] = useState({
    totalSealsLoaded: 0,
    availableSeals: 0,
    sealsInContainers: 0,
  });
  const [boxMetrics, setBoxMetrics] = useState({
    totalBoxes: 0,
    boxesAssigned: 0,
    boxesUnassigned: 0,
    percentageAssigned: 0,
  });

  const [companies, setCompanies] = useState([]);
  const [depots, setDepots] = useState([]);
  const [devices, setDevices] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [failedScans, setFailedScans] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapViewState, setMapViewState] = useState({
    viewLevel: 'country',
    countryFilter: '',
    companyFilter: '',
    countryList: [],
    companyList: [],
    depotList: [],
  });
  const [pieHovered, setPieHovered] = useState(false);
  const [legendMode, setLegendMode] = useState('counts'); // 'counts' | 'percent'

  useEffect(() => {
    // Load initial dashboard data once on mount (no auto-refresh)
    fetchInitialData();
    fetchBatches();
  }, []);

  // When company or batch filters change, refetch devices/assignments so metrics and map reflect filter
  useEffect(() => {
    fetchInitialData();
  }, [filters.companyId, filters.uploadBatchId]);

  // Re-fetch batches when current user/company context changes (cliente scope)
  useEffect(() => {
    fetchBatches();
  }, [currentUser?.company_id, isClient]);

  useEffect(() => {
    calculateMetrics();
    fetchNotificationsAndScans();
  }, [devices, assignments, filters]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const requests = [
        // request devices with optional filtering so we can scope by company and upload batch
        devicesAPI.getAll({
          ...(isClient && currentUser?.company_id ? { company_id: currentUser.company_id } : {}),
          ...(filters.companyId ? { company_id: parseInt(filters.companyId) } : {}),
          ...(filters.uploadBatchId ? { upload_batch_id: filters.uploadBatchId } : {}),
        }),
        boxAssignmentsAPI.getAll(),
      ];

      if (!isClient) {
        requests.unshift(companiesAPI.getAll());
      }

      const results = await Promise.all(requests);
      const companiesRes = !isClient ? results[0] : null;
      const devicesRes = !isClient ? results[1] : results[0];
      const assignmentsRes = !isClient ? results[2] : results[1];

      const companiesList = companiesRes?.data || [];
      const devicesList = devicesRes.data || [];
      const assignmentsList = assignmentsRes.data || [];

      setCompanies(companiesList);
      setDevices(devicesList);
      setAssignments(assignmentsList);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Error loading dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async () => {
    try {
      const params = isClient && currentUser?.company_id
        ? { company_id: currentUser.company_id }
        : {};
      const resp = await devicesAPI.getBatches(params);
      setBatches(normalizeBatches(resp.data || []));
    } catch (error) {
      console.error('Error fetching batches:', error);
      setBatches([]);
    }
  };

  const calculateMetrics = () => {
    let filtered = [...devices];

    if (isClient && currentUser?.company_id) {
      filtered = filtered.filter(d => d.company_id === currentUser.company_id);
    }

    // Filter by date range
    if (filters.startDate) {
      filtered = filtered.filter(d => {
        const createdDate = new Date(d.created_at || d.created_date);
        return createdDate >= new Date(filters.startDate);
      });
    }
    if (filters.endDate) {
      filtered = filtered.filter(d => {
        const createdDate = new Date(d.created_at || d.created_date);
        const endDate = new Date(filters.endDate);
        endDate.setHours(23, 59, 59);
        return createdDate <= endDate;
      });
    }

    // Filter by company
    if (filters.companyId) {
      filtered = filtered.filter(d => d.company_id === parseInt(filters.companyId));
    }

    if (filters.uploadBatchId) {
      filtered = filtered.filter(d => d.upload_batch_id === filters.uploadBatchId);
    }

    const totalSeals = filtered.length;
    // Cliente users consider any seal without a container available, even when assigned to a depot.
    const availableSeals = isClient
      ? filtered.filter(d => !d.container_number || !d.container_number.trim()).length
      : totalSeals - filtered.filter(d => d.depot_id).length;
    const sealsInContainers = filtered.filter(d => d.container_number && d.container_number.trim()).length;

    const uniqueBoxes = new Set(filtered.filter(d => d.box).map(d => d.box));
    const totalBoxes = uniqueBoxes.size;
    const boxesWithAssignment = new Set();
    filtered.forEach(d => {
      if (d.box && (d.depot_id || d.box_assignment_depot_id)) {
        boxesWithAssignment.add(d.box);
      }
    });
    const boxesAssigned = boxesWithAssignment.size;
    const boxesUnassigned = totalBoxes - boxesAssigned;
    const percentageAssigned = totalBoxes > 0 ? Math.round((boxesAssigned / totalBoxes) * 100) : 0;

    setMetrics({
      totalSealsLoaded: totalSeals,
      availableSeals,
      sealsInContainers,
    });
    setBoxMetrics({
      totalBoxes,
      boxesAssigned,
      boxesUnassigned,
      percentageAssigned,
    });
  };

  const fetchNotificationsAndScans = async () => {
    try {
      // Build query params
      const queryParams = {
        limit: 5,
      };

      if (filters.startDate) {
        queryParams.start_date = filters.startDate;
      }
      if (filters.endDate) {
        queryParams.end_date = filters.endDate;
      }

      if (filters.uploadBatchId) {
        queryParams.upload_batch_id = filters.uploadBatchId;
      }

      // For cliente dashboard, always scope notifications to the user's company
      if (isClient && currentUser?.company_id) {
        queryParams.company_id = currentUser.company_id;
      } else if (filters.companyId) {
        queryParams.company_id = parseInt(filters.companyId);
      }

      // Fetch all notifications with filters
      const notificationsRes = await notificationsAPI.getAll(queryParams);
      const notificationsList = notificationsRes.data || [];

      // Extract failed scans from notifications (error or warning type)
      const failed = notificationsList.filter(n =>
        (n.notification_type === 'error' || n.notification_type === 'warning') &&
        (n.message?.includes('not found') || n.message?.includes('failed'))
      );

      setNotifications(notificationsList);
      setFailedScans(failed);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Don't show toast error for notifications - it's secondary data
      setNotifications([]);
      setFailedScans([]);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      companyId: '',
      depotId: '',
      uploadBatchId: '',
    });
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).filter(val => val !== '').length;
  };

  const computePieData = () => {
    const { viewLevel, countryFilter, companyFilter, countryList, companyList, depotList } = mapViewState;

    let items = [];
    if (viewLevel === 'company') {
      items = companyList.filter(c => !countryFilter || c.country === countryFilter);
    } else if (viewLevel === 'depot') {
      items = depotList.filter(d => {
        if (countryFilter && d.country !== countryFilter) return false;
        if (companyFilter && d.company !== companyFilter) return false;
        return true;
      });
    } else {
      items = countryList;
    }

    const totals = items.reduce(
      (acc, item) => {
        acc.available += item.available || 0;
        if (!isClient) {
          acc.awaiting += item.awaitingContainer || 0;
        }
        acc.inUse += item.inUse || 0;
        return acc;
      },
      { available: 0, awaiting: 0, inUse: 0 }
    );

    const activeTotal = totals.available + totals.awaiting + totals.inUse;
    if (activeTotal === 0) {
      return {
        hasData: false,
        label: '',
        totals,
        percents: { available: 0, awaiting: 0, inUse: 0 },
      };
    }

    const percents = {
      available: Math.round((totals.available / activeTotal) * 100),
      awaiting: isClient ? 0 : Math.round((totals.awaiting / activeTotal) * 100),
      inUse: Math.round((totals.inUse / activeTotal) * 100),
    };

    let contextLabel = 'All countries';
    if (viewLevel === 'company') {
      contextLabel = countryFilter ? `Companies in ${countryFilter}` : 'All companies';
    } else if (viewLevel === 'depot') {
      if (companyFilter) {
        contextLabel = `${companyFilter} depots`;
      } else if (countryFilter) {
        contextLabel = `Depots in ${countryFilter}`;
      } else {
        contextLabel = 'All depots';
      }
    }

    return {
      hasData: true,
      label: contextLabel,
      totals,
      percents,
    };
  };

  if (loading) {
    return (
      <div className="superadmin-dashboard">
        <div className="container">
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const pieData = computePieData();
  const pieStyle = pieData.hasData
    ? isClient
      ? {
          backgroundImage: `conic-gradient(#10b981 0 ${pieData.percents.available}%, #ef4444 ${pieData.percents.available}% 100%)`,
        }
      : {
          backgroundImage: `conic-gradient(#10b981 0 ${pieData.percents.available}%, #f59e0b ${pieData.percents.available}% ${pieData.percents.available + pieData.percents.awaiting}%, #ef4444 ${pieData.percents.available + pieData.percents.awaiting}% 100%)`,
        }
    : { backgroundColor: '#f5f5f5' };

  return (
    <div className="superadmin-dashboard">
      <div className="container">
        <div className="dashboard-header">
          <button
            className="settings-btn"
            onClick={() => setShowFiltersPanel(!showFiltersPanel)}
            title="Open filters"
          >
            ?
            {getActiveFilterCount() > 0 && (
              <span className="filter-badge">{getActiveFilterCount()}</span>
            )}
          </button>
        </div>

        <div className={`filters-panel ${showFiltersPanel ? 'open' : ''}`}>
          <div className="filters-panel-header">
            <h2>Filters</h2>
            <button
              className="close-btn"
              onClick={() => setShowFiltersPanel(false)}
              title="Close filters"
            >
              ?
            </button>
          </div>

          <div className="filters-panel-content">
            <div className="filter-group">
              <label>Start Date</label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-group">
              <label>End Date</label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
              />
            </div>

            {!isClient && (
              <div className="filter-group">
                <label>Company</label>
                <select
                  name="companyId"
                  value={filters.companyId}
                  onChange={handleFilterChange}
                >
                  <option value="">All Companies</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="filter-group">
              <label>Depot</label>
              <select
                name="depotId"
                value={filters.depotId}
                onChange={handleFilterChange}
              >
                <option value="">All Depots</option>
                {depots.map((depot) => (
                  <option key={depot.id} value={depot.id}>
                    {depot.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Upload Batch</label>
              <select
                name="uploadBatchId"
                value={filters.uploadBatchId}
                onChange={handleFilterChange}
              >
                <option value="">All Batches</option>
                {batches.map((batch) => (
                  <option key={batch.upload_batch_id} value={batch.upload_batch_id}>
                    {batch.label} - {batch.device_count} devices
                  </option>
                ))}
              </select>
            </div>

            <button className="btn-reset-panel" onClick={resetFilters}>
              Reset Filters
            </button>
          </div>
        </div>

        {isClient && (
          <div className="kpi-cards client-box-kpis">
            <div className="kpi-card">
              <div className="kpi-label">Total Boxes<span className="filter-note">(Filterable)</span></div>
              <div className="kpi-value">{boxMetrics.totalBoxes.toLocaleString()}</div>
              <div className="kpi-description">Boxes in this company</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-label">Boxes Assigned</div>
              <div className="kpi-value">{boxMetrics.boxesAssigned.toLocaleString()}</div>
              <div className="kpi-description">Assigned to depots</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-label">Boxes Unassigned</div>
              <div className="kpi-value">{boxMetrics.boxesUnassigned.toLocaleString()}</div>
              <div className="kpi-description">Waiting for assignment</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-label">Assignment %<span className="filter-note">(Filterable)</span></div>
              <div className="kpi-value">{boxMetrics.percentageAssigned}%</div>
              <div className="kpi-description">Boxes assigned to depots</div>
            </div>
          </div>
        )}

        <div className="superadmin-main-row">
          <div className="superadmin-main-full">
            <MapWidget
              onViewStateChange={setMapViewState}
              lockedViewLevel={isClient ? 'depot' : undefined}
              hideCountryFilter={isClient}
              hideCompanyFilter={isClient}
              hideDepotLocationColumns={isClient}
              hideAwaitingContainer={isClient}
              clientAvailabilityMode={isClient}
              uploadBatchId={filters.uploadBatchId}
            />
          </div>
        </div>

        <div className="dashboard-grid">
          <div className="chart-section">
            <h2>Seals Distribution (by current map level)</h2>
            <div className="legend-toggle">
              <span>Show as:</span>
              <button
                type="button"
                className={legendMode === 'counts' ? 'legend-toggle-btn active' : 'legend-toggle-btn'}
                onClick={() => setLegendMode('counts')}
              >
                Counts
              </button>
              <button
                type="button"
                className={legendMode === 'percent' ? 'legend-toggle-btn active' : 'legend-toggle-btn'}
                onClick={() => setLegendMode('percent')}
              >
                %
              </button>
            </div>
            {!pieData.hasData ? (
              <p className="no-data">No data available for the current map view.</p>
            ) : (
              <div className="pie-wrapper">
                <div
                  className="pie-chart-visual"
                  style={pieStyle}
                  onMouseEnter={() => setPieHovered(true)}
                  onMouseLeave={() => setPieHovered(false)}
                >
                  <div className="pie-center-label">
                    <span className="pie-total">{pieData.totals.available + pieData.totals.awaiting + pieData.totals.inUse}</span>
                    <span className="pie-caption">Total seals</span>
                  </div>
                  {pieHovered && pieData.hasData && (
                    <div className="pie-hover-card">
                      <div className="pie-hover-title">Current breakdown</div>
                      <div className="pie-hover-row">Available: {pieData.totals.available} ({pieData.percents.available}%)</div>
                      {!isClient && (
                        <div className="pie-hover-row">Awaiting: {pieData.totals.awaiting} ({pieData.percents.awaiting}%)</div>
                      )}
                      <div className="pie-hover-row">In use: {pieData.totals.inUse} ({pieData.percents.inUse}%)</div>
                    </div>
                  )}
                </div>
                <div className="pie-legend">
                  <div className="pie-context">{pieData.label}</div>
                  <div className="pie-legend-item">
                    <span className="legend-color available"></span>
                    <span>
                      {legendMode === 'counts'
                        ? `Available: ${pieData.totals.available}`
                        : `Available: ${pieData.percents.available}%`}
                    </span>
                  </div>
                  {!isClient && (
                    <div className="pie-legend-item">
                      <span className="legend-color awaiting"></span>
                      <span>
                        {legendMode === 'counts'
                          ? `Awaiting container: ${pieData.totals.awaiting}`
                          : `Awaiting container: ${pieData.percents.awaiting}%`}
                      </span>
                    </div>
                  )}
                  <div className="pie-legend-item">
                    <span className="legend-color in-use"></span>
                    <span>
                      {legendMode === 'counts'
                        ? `In use: ${pieData.totals.inUse}`
                        : `In use: ${pieData.percents.inUse}%`}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="alerts-section">
            {/* Notifications */}
            <div className="alerts-card">
              <div className="alerts-header">
                <h3>Recent Notifications</h3>
                <span className="badge">{notifications.length}</span>
              </div>
              <div className="alerts-list">
                {notifications.length === 0 ? (
                  <p className="no-data">No notifications</p>
                ) : (
                  notifications.map(notif => (
                    <div key={notif.id} className="alert-item">
                      <div className="alert-time">
                        {formatDateForTimezone(notif.created_at, timezone)}
                      </div>
                      <div className="alert-message">{notif.message}</div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Failed Scans - visible only for non-cliente users */}
            {!isClient && (
              <div className="alerts-card">
                <div className="alerts-header">
                  <h3>Failed Scans</h3>
                  <span className="badge danger">{failedScans.length}</span>
                </div>
                <div className="alerts-list">
                  {failedScans.length === 0 ? (
                    <p className="no-data">No failed scans</p>
                  ) : (
                    failedScans.map(scan => (
                      <div key={scan.id} className="alert-item failed">
                        <div className="alert-icon">?</div>
                        <div className="alert-details">
                          <div className="alert-time">
                            {formatDateForTimezone(scan.created_at, timezone)}
                          </div>
                          <div className="alert-message">{scan.message}</div>
                          {scan.user_id && (
                            <div className="alert-meta">User ID: {scan.user_id}</div>
                          )}
                          {scan.company_id && (
                            <div className="alert-meta">
                              Company: {companies.find(c => c.id === scan.company_id)?.name}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
